import _ from "lodash";
import { CSSProperties, FC, ReactEventHandler, useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Image,
  ImageErrorEventData,
  ImageProps,
  ImageStyle,
  ImageURISource,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { kLoadingSpinner } from "src/util/constants";
import { getSrcSet, getSrcSetString } from "src/util/functions";
import { useInterval } from "usehooks-ts";
import { PdAlert } from "./Icons";

const MAX_LOAD_ATTEMPTS = 4;

export type tReactiveImageProps = {
  style?: () => never;
  source: ImageURISource;
  onLoad?: () => void;
  webStyle?: CSSProperties;
  mobileStyle?: StyleProp<ImageStyle>;
  defaultSource?: { uri: string };
  resizeMode?: "cover" | "contain" | "stretch" | "repeat" | "center";
  onError?: (error: NativeSyntheticEvent<ImageErrorEventData>) => void;
  showFallbackLabel?: boolean;
};

const mobile2webResizeMode = {
  stretch: "fill",
  contain: "contain",
  cover: "cover",
  center: "scale-down",
  repeat: "none",
} as const;

const Fallback: FC<{ isLoading?: boolean; isError?: boolean; showLabel?: boolean }> = ({
  isLoading,
  isError,
  showLabel,
}) => {
  if (isLoading) {
    return (
      <View style={styles.fallback}>
        <ActivityIndicator size="small" color={kColors.brandDark} />
        {showLabel && <Text style={[styles.fallbackLabel, styles.mt1]}>Loading</Text>}
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.fallback}>
        <PdAlert color={kColors.brandDark} />
        {showLabel && <Text style={[styles.fallbackLabel, styles.mt1]}>Load failed</Text>}
      </View>
    );
  }

  return null;
};

const WebImage: FC<{
  source: string;
  isTrying: boolean;
  isLoading?: boolean;
  isError?: boolean;
  showFallbackLabel?: boolean;
  style?: CSSProperties;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
}> = ({ source, isTrying, isLoading, isError, showFallbackLabel, style, onLoad, onError }) => {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (isTrying && isMounted) {
        const _src = await getSrcSetString(source);
        if (isMounted) {
          setSrc(_src);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [source, isTrying]);

  return (
    <>
      {src && isTrying && <img style={style} srcSet={src} onLoad={onLoad} onError={onError} />}
      <Fallback isLoading={isLoading} isError={isError} showLabel={showFallbackLabel} />
    </>
  );
};

const MobileImage: FC<Omit<ImageProps, "source"> & { source: string }> = ({ source, ...rest }) => {
  const [uri, setUri] = useState("");

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const src = await getSrcSet(source);
      if (isMounted) {
        setUri(src[2048]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [source]);

  return <Image source={{ uri }} defaultSource={{ uri: kLoadingSpinner }} {...rest} />;
};

/**
 * SLImage will use a srcset to load images, if there is an error it will try to load 3 times and then fail.
 *
 */
export const SLImage: FC<tReactiveImageProps> = ({
  source,
  onLoad,
  webStyle,
  mobileStyle,
  resizeMode,
  onError,
  showFallbackLabel,
}) => {
  const [sTryLoad, _setTryLoad] = useState<boolean>(true);
  const [sErrorCount, _setErrorCount] = useState<number>(0);
  const aDeadFlag = !(sErrorCount < MAX_LOAD_ATTEMPTS);

  const _handleError = useCallback<ReactEventHandler<HTMLImageElement>>(
    (/* e */) => {
      // _setLoadingState(true);
      _setErrorCount((count) => {
        return count + 1;
      });
      _setTryLoad(false);
    },
    [],
  );

  const _onLoad = useCallback(() => {
    // _setLoadingState(false);
    if (sErrorCount > 0) _setErrorCount(0);
    if (onLoad) {
      onLoad();
    }
  }, [onLoad, sErrorCount]);

  useInterval(
    () => {
      if (!sTryLoad) {
        _setTryLoad(true);
      }
    },
    // Delay in milliseconds or null to stop it
    aDeadFlag ? null : 2000,
  );

  const webStyles: { [key: string]: CSSProperties } = {
    aspectRatio: {
      objectFit: mobile2webResizeMode[resizeMode || "cover"],
      margin: 0,
      width: "100%",
      height: "100%",
    },
  } as const;

  if (!source.uri?.includes("googleapis.com")) {
    return <Image style={mobileStyle} source={source} />;
  }

  if (Platform.OS === "web") {
    return (
      <View style={[styles.container, mobileStyle]}>
        <WebImage
          source={source.uri}
          isTrying={sTryLoad}
          isLoading={sErrorCount > 0 && !aDeadFlag}
          isError={aDeadFlag}
          showFallbackLabel={showFallbackLabel}
          style={{ ...webStyle, ...webStyles.aspectRatio }}
          onLoad={_onLoad}
          onError={_handleError}
        />
      </View>
    );
  } else
    return (
      <MobileImage source={source.uri} style={mobileStyle} onLoad={onLoad} resizeMode={resizeMode} onError={onError} />
    );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "stretch",
    backgroundColor: kColors.almostWhite,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    justifyContent: "center",
    overflow: "hidden",
  },
  fallback: {
    alignItems: "center",
    bottom: 0,
    flex: 1,
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  fallbackLabel: {
    fontFamily: "Poppins",
    fontSize: 10,
    textAlign: "center",
  },
  mt1: {
    marginTop: kStyleConst.kSpacing,
  },
});
